import { Loader2 } from "lucide-react";
import { useRef } from "react";

import { Chip, DrawerShell, ProvenanceBadge } from "@/components/comms";
import {
  type CustomerOrder,
  type EmailRecord,
  EVENT_LABELS,
  OPT_OUT_REASON,
  useOrderEmails,
} from "@/lib/comms";
import { cn, formatShortDateTime } from "@/lib/utils";

interface EmailCardProps {
  email: EmailRecord;
}

function EmailCard({ email }: EmailCardProps) {
  const suppressed = email.status === "suppressed";
  return (
    <div
      className={cn(
        "border-wg-03 rounded-xl border p-4",
        suppressed ? "bg-wg-01 border-dashed" : "bg-white",
      )}
    >
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="flex items-center gap-2">
          <span
            className={cn("text-[14px] font-semibold", suppressed ? "text-wg-06" : "text-wg-08")}
          >
            {EVENT_LABELS[email.event_type]}
          </span>
          <ProvenanceBadge provenance={email.provenance} />
        </span>
        <span className="text-wg-06 text-xs tabular-nums">
          {formatShortDateTime(email.sent_at)}
        </span>
      </div>
      {suppressed ? (
        <div className="text-wg-06 flex items-center gap-2 text-[12.5px]">
          <Chip>Suppressed</Chip>
          <span>{email.suppression_reason}</span>
        </div>
      ) : (
        <>
          <div className="text-wg-06 text-[12px] leading-relaxed">
            <b className="text-wg-07">To:</b> {email.to}
            <br />
            <b className="text-wg-07">From:</b> {email.from}
            {email.cc && (
              <>
                <br />
                <b className="text-wg-07">CC:</b> {email.cc}
              </>
            )}
          </div>
          <p className="text-wg-07 mt-2 text-[13px] leading-relaxed">{email.body}</p>
        </>
      )}
    </div>
  );
}

interface EmailLogDrawerProps {
  /** Order whose log to show; null keeps the drawer closed. */
  order: CustomerOrder | null;
  customerName: string;
  onClose: () => void;
}

export function EmailLogDrawer({ order, customerName, onClose }: EmailLogDrawerProps) {
  // Retain the last order so the drawer's content stays put during the exit
  // slide (order flips to null the moment the close starts).
  const lastOrderRef = useRef<CustomerOrder | null>(order);
  if (order) {
    lastOrderRef.current = order;
  }
  const displayOrder = order ?? lastOrderRef.current;

  const emailsQuery = useOrderEmails(displayOrder?.id ?? null);
  const emails = emailsQuery.data ?? [];
  // Mockup shows one explanatory line for opted-out accounts; the suppressed
  // cards below it keep the audit trail the mockup didn't have.
  const optedOut =
    emails.length > 0 && emails.every((e) => e.suppression_reason === OPT_OUT_REASON);

  return (
    <DrawerShell
      open={order !== null}
      onClose={onClose}
      title="Sent emails log"
      sub={displayOrder ? `Order #${displayOrder.number}` : undefined}
    >
      {emailsQuery.isLoading ? (
        <Loader2 className="text-wg-05 mx-auto mt-8 size-5 animate-spin" />
      ) : emails.length === 0 ? (
        <div className="bg-wg-02 text-wg-06 rounded-xl p-5 text-center text-sm">
          No emails sent for this order yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {optedOut && displayOrder && (
            <div className="bg-wg-02 text-wg-06 rounded-xl p-5 text-sm">
              {customerName} has opted out of proactive order emails, so none have been sent for
              order #{displayOrder.number}.
            </div>
          )}
          {emails.map((email, i) => (
            <EmailCard key={`${email.event_type}-${i}`} email={email} />
          ))}
        </div>
      )}
    </DrawerShell>
  );
}
